import { useMemo } from 'react'
import * as XLSX from 'xlsx'
import type { EstoqueRow } from '../../hooks/useEstoqueAnalytics'

interface Props {
  rows: EstoqueRow[]
  cutoffDays?: number
}

const MS_PER_DAY = 1000 * 60 * 60 * 24

function brl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

export default function EncalheList({ rows, cutoffDays = 90 }: Props) {
  const cutoffMs = useMemo(() => Date.now() - cutoffDays * MS_PER_DAY, [cutoffDays])

  const items = useMemo(() => {
    const now = Date.now()
    return rows
      .filter(r => {
        if (r.current_stock <= 0) return false
        if (!r.last_sale_at) return false
        return new Date(r.last_sale_at).getTime() < cutoffMs
      })
      .map(r => {
        const lastSaleMs = new Date(r.last_sale_at!).getTime()
        const daysSince = Math.floor((now - lastSaleMs) / MS_PER_DAY)
        const value = (r.current_stock ?? 0) * (r.cost_avg ?? 0)
        return { ...r, daysSince, value }
      })
      .sort((a, b) => b.daysSince - a.daysSince) // mais antigos primeiro
  }, [rows, cutoffMs])

  const totalValue = useMemo(() => items.reduce((s, i) => s + i.value, 0), [items])

  function handleExport() {
    if (items.length === 0) return
    const data = items.map(it => ({
      SKU: it.sku,
      Produto: it.name,
      Saldo: it.current_stock,
      'Custo médio': it.cost_avg ?? 0,
      'Valor parado': it.value,
      'Última venda': it.last_sale_at ? fmtDate(it.last_sale_at) : '—',
      'Dias sem vender': it.daysSince,
    }))
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Encalhe')
    XLSX.writeFile(wb, `encalhe_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  return (
    <div className="bg-surface border border-yellow-800/40 rounded-xl flex flex-col h-full">
      <div className="p-4 border-b border-line">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-yellow-200 uppercase tracking-wide">Encalhe</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-faint">{items.length} SKUs</span>
            <button
              onClick={handleExport}
              disabled={items.length === 0}
              className="px-2.5 py-1 bg-sunken hover:bg-sunken/80 disabled:opacity-40 disabled:cursor-not-allowed text-ink-muted text-xs font-medium rounded-md transition-colors"
              title="Exportar lista de encalhe para Excel"
            >
              ⬇ Excel
            </button>
          </div>
        </div>
        <p className="text-xs text-ink-faint mt-1">
          Estoque parado · Valor: <span className="text-yellow-200 font-medium">{brl(totalValue)}</span>
        </p>
      </div>

      <div className="overflow-auto flex-1 min-h-0">
        {items.length === 0 ? (
          <div className="px-4 py-10 text-center text-ink-faint text-sm">
            Nenhum produto encalhado.
          </div>
        ) : (
          <ul className="divide-y divide-line/60">
            {items.map(it => (
              <li key={it.id} className="px-4 py-2.5 hover:bg-sunken/30 transition-colors">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-mono text-ink-faint shrink-0">{it.sku}</span>
                  <span className="text-xs text-yellow-300 font-medium shrink-0 tabular-nums">
                    {it.daysSince}d sem vender
                  </span>
                </div>
                <p className="text-sm text-ink mt-0.5 truncate" title={it.name}>{it.name}</p>
                <div className="flex items-center justify-between text-xs text-ink-faint mt-1">
                  <span>Saldo {it.current_stock} · Última {fmtDate(it.last_sale_at!)}</span>
                  <span className="text-ink-muted tabular-nums">{brl(it.value)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
